import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldValues,
  type Path
} from 'react-hook-form';
import { cn } from '@library/component-utils/cn';
import { formatVariableToLabel } from '@bo-utils/common';
import { useMemo } from 'react';

export interface FormFieldControllerProps<T extends FieldValues> {
  children: (props: ControllerRenderProps<T, Path<T>>) => React.ReactNode;
  containerClassName?: string;
  control: Control<T>;
  labelClassName?: string;
  labelName?: string;
  name: Path<T>;
  required?: boolean;
  rules?: object;
}

export function FormFieldController<T extends FieldValues>({
  children,
  containerClassName = '',
  control,
  labelClassName = '',
  labelName = '',
  name,
  required = false,
  rules = {}
}: FormFieldControllerProps<T>) {
  const validationRules = useMemo(
    () => ({
      ...rules,
      ...(required
        ? {
            required: `${labelName || formatVariableToLabel(name)} is required`
          }
        : {})
    }),
    [name, required, rules, labelName]
  );

  return (
    <div className={cn('flex w-full flex-col', containerClassName)}>
      {labelName && (
        <div className="mb-1 flex items-center">
          <div className={cn('text-soClean-180 text-xs', labelClassName)}>
            {labelName}
          </div>
          {required && <p className="text-roses self-start px-1 text-xs">*</p>}
        </div>
      )}

      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field, fieldState }) => (
          <>
            {children(field)}

            {fieldState.error && (
              <p
                className="text-roses mt-1 text-xs"
                data-testid={`${name}-validation-error`}
              >
                {fieldState.error.message}
              </p>
            )}
          </>
        )}
      />
    </div>
  );
}
